import {
    endOfDay,
    endOfToday,
    endOfYear,
    formatISO,
    startOfDay,
    startOfToday,
    startOfYear,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';

export type RangePreset =
    | 'TODAY'
    | 'YESTERDAY'
    | 'LAST_WEEK'
    | 'LAST_MONTH'
    | 'LAST_QUARTER'
    | 'TTM'
    | 'YTD'
    | 'FULL_YEAR';

export type CustomTimeRange = {
    from: Date;
    to: Date;
};

export type CustomDateRange = {
    from: string;
    to: string;
};

export type TimeRange = { preset: RangePreset; custom?: never } | { custom: CustomTimeRange; preset?: never };

export type DateRange = { preset: RangePreset; custom?: never } | { custom: CustomDateRange; preset?: never };

const presetRanges = {
    TODAY: () => ({
        from: startOfToday(),
        to: endOfToday(),
    }),
    YESTERDAY: () => {
        const today = startOfToday();
        return {
            from: startOfDay(subDays(today, 1)),
            to: endOfDay(subDays(today, 1)),
        };
    },
    LAST_WEEK: () => ({
        from: startOfDay(subWeeks(startOfToday(), 1)),
        to: endOfToday(),
    }),
    LAST_MONTH: () => ({
        from: startOfDay(subMonths(startOfToday(), 1)),
        to: endOfToday(),
    }),
    LAST_QUARTER: () => ({
        from: startOfDay(subQuarters(startOfToday(), 1)),
        to: endOfToday(),
    }),
    TTM: () => ({
        from: startOfDay(subYears(startOfToday(), 1)),
        to: endOfToday(),
    }),
    YTD: () => ({
        from: startOfYear(startOfToday()),
        to: endOfToday(),
    }),
    FULL_YEAR: () => {
        const today = startOfToday();
        return {
            from: startOfYear(today),
            to: endOfYear(today),
        };
    },
} satisfies Record<RangePreset, () => CustomTimeRange>;

function getPresetRange(preset: RangePreset): CustomTimeRange {
    return presetRanges[preset]();
}

export function normaliseTimeRange(range: TimeRange): CustomTimeRange {
    return range.preset ? getPresetRange(range.preset) : range.custom;
}

export function normaliseDateRange(range: DateRange): CustomDateRange {
    if (range.custom) {
        return range.custom;
    }

    const { from, to } = getPresetRange(range.preset);

    return { from: toIsoDate(from), to: toIsoDate(to) };
}

function toIsoDate(date: Date): string {
    return formatISO(date, { representation: 'date' });
}
